import { isAxiosError } from 'axios';

// Quota-exceeded plumbing (story 2-16, P02 §8). The backend's quota framework
// (2-15) answers an over-cap request with 402 whose body carries the standard
// quota_exceeded shape { reason:'quota', metric, used, limit, period,
// upgradeUrl } flattened next to { code, message }. The legacy apiClient axios
// instance detects that shape in a response interceptor and emits it here; the
// DiscoveryProvider subscribes and opens the QuotaTeaser — so every call path
// on the shared instance surfaces "N of M used → upgrade" instead of a raw
// 402, without per-call wiring.

export interface QuotaPeriod {
  start: string | null;
  end: string | null;
}

export interface QuotaExceeded {
  metric: string;
  used: number;
  limit: number;
  period: QuotaPeriod | null;
  upgradeUrl?: string;
}

/** Narrow a 402 response body to the 2-15 quota_exceeded shape. */
export function isQuotaExceededBody(data: unknown): data is QuotaExceeded & { reason: 'quota' } {
  if (typeof data !== 'object' || data === null) return false;
  const body = data as Record<string, unknown>;
  return (
    body.reason === 'quota' &&
    typeof body.metric === 'string' &&
    typeof body.used === 'number' &&
    typeof body.limit === 'number'
  );
}

/** True when an error is a quota_exceeded 402 (already routed to the teaser). */
export function isQuotaError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 402 && isQuotaExceededBody(error.response.data);
}

type QuotaListener = (quota: QuotaExceeded) => void;

const listeners = new Set<QuotaListener>();

export function subscribeQuotaExceeded(listener: QuotaListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitQuotaExceeded(quota: QuotaExceeded): void {
  listeners.forEach((listener) => listener(quota));
}
